import moment from 'moment'
import Vue from 'vue'

import { ToOrder, ToCartProductOrderLine, ToCartProductOptionOrderLine } from '@/entities/ToOrder'
import { OrderLine } from '@/entities/Order'
import { getPricingGroupWithPricesAppliedToAccount } from '@/lib/helpers/AccountUtils'
import { findOptionById } from '@/lib/helpers/CatalogUtils'
import { unwatch } from '@/lib/helpers/Vue'
import { orderLineTypes, orderLineItemEntities } from '@/config'

const state = {
  selectedProduct: null,
  products: [],
  cartProducts: [],
  tempCartProducts: [],
}

const getters = {
  getTotal: (state) => ({ currencies, products, noProducts, reservationOrderLines }) => {
    const productArray = noProducts ? [] : (products || state.cartProducts)
    return getTotalBalanceByCurrency(productArray, currencies, reservationOrderLines)
  },
  getTempTotal: (state) => (currencies, reservationOrderLines) => {
    return getTotalBalanceByCurrency(state.tempCartProducts, currencies, reservationOrderLines)
  },
  productsTotal: (state) => {
    let total = 0
    state.products.forEach((stateProduct) => {
      const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(stateProduct.product.pricingGroupsWithPrices)
      total += pricingGroupWithPrices.prices[0][0].amount * stateProduct.quantity
      stateProduct.selectedOptions.forEach((optionId) => {
        const option = findOptionById({ product: stateProduct.product, optionId })
        const pGP = getPricingGroupWithPricesAppliedToAccount(option.price)
        total += pGP.prices[0][0].amount * stateProduct.quantity
      })
    })
    return total
  },
  getOrder: (state, getters, rootState) => (reservationOrderLines) => {
    return new ToOrder({
      cartProducts: state.cartProducts,
      selectedOptions: state.selectedOptions,
      reservationOrderLines: reservationOrderLines.map(reservationOrderLine => {
        return {
          ...reservationOrderLine,
          addedAt: moment().toISOString(),
        }
      }),
      stationId: rootState.activities.extendedActivity.activity.stationId,
    })
  },

  cartProductOrderLines: (state) => (toggleProducts = true, toggleServiceCharge = false) => {
    var orderLines = []

    state.cartProducts.forEach((cartProduct) => {
      const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(cartProduct.product.pricingGroupsWithPrices)
      const productOrderLine = new ToCartProductOrderLine({
        ...cartProduct,
        name: cartProduct.product.name,
        price: pricingGroupWithPrices.prices[0],
      })

      let totalPriceOptions = 0 

      if (toggleProducts) {
        orderLines.push(productOrderLine)
        cartProduct.optionIds.forEach((optionId) => {
          const option = findOptionById({ product: cartProduct.product, optionId })
          const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(option.price)

          const optionOrderLine = new ToCartProductOptionOrderLine({
            ...cartProduct,
            optionId,
            parentOrderLine: productOrderLine.id,
            price: pricingGroupWithPrices.prices[0],
          })

          totalPriceOptions += optionOrderLine.price[0].amount
          
          orderLines.push(optionOrderLine)
        })
      }

      if (toggleServiceCharge && cartProduct.product && cartProduct.product.serviceCharge) {
        const serviceCharge = cartProduct.product.serviceCharge

        const percentTaxGroup = cartProduct.product.taxes.reduce((percentTotal, { value }) => percentTotal + value, 0) / 100
        const percentServiceCharge = serviceCharge.value / 100
        
        const pricePercentServiceCharge = (((productOrderLine.price[0].amount +  totalPriceOptions) / (1 + percentTaxGroup)) * percentServiceCharge)

        const orderLineServiceCharge = new OrderLine({
          itemEntity: orderLineItemEntities.PRODUCT,
          orderLineType: orderLineTypes.SERVICE_CHARGE,
          value: serviceCharge.value,
          parentOrderLine: cartProduct.product.id,
          price: [
            {
              amount: pricePercentServiceCharge,
              currency: productOrderLine.price[0].currency,
            },
          ],
          quantity: productOrderLine.quantity,
        })

        orderLines.push(orderLineServiceCharge)
      }
    })

    return orderLines
  },
  orderLines: (state) => {
    return state.cartProducts.map((cartProduct) => {
      return {
        ...cartProduct,
        name: cartProduct.product.name,
      }
    })
  },
  mainStateProduct: state => state.products.find(stateProduct => !stateProduct.isRelatedProduct),
  getBasicProductCartQuantity: state => productId => {
    const basicProductInCart = state.cartProducts.find((cartProduct) => cartProduct.product.id === productId && !cartProduct.optionIds.length)
    return basicProductInCart ? basicProductInCart.quantity : 0
  },
}

const actions = {}

const mutations = {
  setSelectedProduct (state, product) {
    Vue.set(state, 'selectedProduct', product)
  },
  addProductToState (state, { product, isRelatedProduct }) {
    if (state.products.findIndex((stateProduct) => stateProduct.product.id === product.id) !== -1) return
    const newStateProduct = {
      product,
      selectedOptions: [],
      isRelatedProduct: !!isRelatedProduct,
      quantity: 0,
    }
    let products = [...state.products, newStateProduct]
    Vue.set(state, 'products', products)
  },
  toggleSelectedOption (state, { optionId, productId }) {
    let products = unwatch(state.products)
    let product = products.find(({ product }) => product.id === productId)
    if (!product.selectedOptions.includes(optionId)) {
      product.selectedOptions.push(optionId)
    } else {
      product.selectedOptions = product.selectedOptions.filter(value => value !== optionId)
    }
    Vue.set(state, 'products', products)
  },
  removeSelectedOption (state, { optionId, productId }) {
    let products = unwatch(state.products)
    let product = products.find(({ product }) => product.id === productId)
    product.selectedOptions = product.selectedOptions.filter(value => value !== optionId)
    Vue.set(state, 'products', products)
  },
  setQuantity (state, { quantity, productId }) {
    let products = unwatch(state.products)
    let stateProduct = products.find(({ product }) => product.id === productId)
    stateProduct.quantity = quantity
    Vue.set(state, 'products', products)
    var cart = modifyCart(state.tempCartProducts)
    Vue.set(state, 'tempCartProducts', cart)
  },
  resetProducts (state) {
    Vue.set(state, 'products', [])
    Vue.set(state, 'tempCartProducts', state.cartProducts)
  },
  resetTempCartProducts (state) {
    Vue.set(state, 'tempCartProducts', state.cartProducts)
  },
  updateCartProducts (state) {
    var cart = modifyCart(state.cartProducts)
    Vue.set(state, 'cartProducts', cart)
    Vue.set(state, 'tempCartProducts', cart)
  },
  removeProductInCart (state, { productId, optionIds }) {
    var newCartProducts = state.cartProducts.filter((cartProduct) => {
      return cartProduct.product.id !== productId ||
        cartProduct.optionIds.length !== optionIds.length ||
        cartProduct.optionIds.some(optionId => !optionIds.includes(optionId))
    })
    Vue.set(state, 'cartProducts', newCartProducts)
    Vue.set(state, 'tempCartProducts', newCartProducts)
  },
  resetCart (state) {
    Vue.set(state, 'product', null)
    Vue.set(state, 'quantity', 0)
    Vue.set(state, 'cartProducts', [])
    Vue.set(state, 'tempCartProducts', [])
  },
}

function getTotalBalanceByCurrency (baseCart, currencies, reservationOrderLines = []) {
  var totalByCurrency = []
  baseCart.forEach(cartProduct => {
    const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(cartProduct.product.pricingGroupsWithPrices)
    addToTotal(pricingGroupWithPrices.prices[0][0], cartProduct.quantity)
    cartProduct.optionIds.forEach(optionId => {
      const option = findOptionById({ product: cartProduct.product, optionId })
      const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(option.price)
      addToTotal(pricingGroupWithPrices.prices[0][0], cartProduct.quantity)
    })
  })
  reservationOrderLines.forEach((reservationOrderLine) => {
    reservationOrderLine.price.forEach(price => {
      addToTotal(price, reservationOrderLine.quantity)
    })
  })
  function addToTotal (price, quantity) {
    const currencyId = price.currency ? price.currency.id : price.currencyId
    var index = totalByCurrency.findIndex(item => { return item.currency.id === currencyId })
    if (index !== -1) {
      var existingCurrency = totalByCurrency.find(item => { return item.currency.id === currencyId })
      totalByCurrency.splice(index, 1, {
        currency: existingCurrency.currency,
        amount: existingCurrency.amount + (price.amount * quantity),
      })
    } else {
      totalByCurrency.push({
        currency: currencies ? currencies.find(c => { return c.id === currencyId }) : price.currency,
        amount: price.amount * quantity,
      })
    }
  }
  return totalByCurrency
}

function modifyCart (baseCart) {
  let cart = unwatch(baseCart)
  state.products.forEach((stateProduct) => {
    const cartProduct = {
      product: stateProduct.product,
      quantity: stateProduct.quantity,
      optionIds: stateProduct.selectedOptions,
      isRelatedProduct: stateProduct.isRelatedProduct,
    }
    const index = cart.findIndex(p => {
      return p.product.id === stateProduct.product.id &&
        (!p.product.displayedModifiers.length ||
          (
            p.optionIds.length === stateProduct.selectedOptions.length &&
            p.optionIds.every(optionId => stateProduct.selectedOptions.includes(optionId))
          ))
    })
    if (index !== -1) {
      if (stateProduct.quantity > 0) {
        if (stateProduct.selectedOptions.length) {
          cart.splice(index, 1, {
            ...cartProduct,
            quantity: stateProduct.quantity + cart[index].quantity,
          })
        } else {
          cart.splice(index, 1, cartProduct)
        }
      } else {
        cart.splice(index, 1)
      }
    } else {
      if (stateProduct.quantity > 0) {
        cart.push(cartProduct)
      }
    }
  })
  return cart
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
